import React, { useState, useEffect, useId } from 'react';
import { getCompletedRoutes } from './api';
import { useTranslation } from '../i18n';

const SLOT_COLORS = { morning: '#F59E0B', afternoon: '#3B82F6', evening: '#8B5CF6', manual: '#6B7280' };
const SLOT_EMOJI = { morning: '☀️', afternoon: '🌤️', evening: '🌙', manual: '📝' };

export default function Reports({ role = 'owner', locations = [] }) {
  const { t, lang } = useTranslation();
  const today = new Date().toISOString().split('T')[0];
  const firstOfMonth = today.slice(0, 7) + '-01';
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportLocation, setReportLocation] = useState('all');
  const [expanded, setExpanded] = useState({});
  const showLocationFilter = role === 'owner' && locations.length > 1;
  const fromId = useId();
  const toId = useId();
  const locId = useId();

  const slotLabel = (slot) => {
    const k = slot && SLOT_EMOJI[slot] ? `reports.${slot}` : null;
    const text = k ? t(k) : (slot || 'Route');
    return `${SLOT_EMOJI[slot] || ''} ${text}`.trim();
  };

  useEffect(() => { loadReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReport = async (locOverride) => {
    setLoading(true);
    const loc = role === 'owner'
      ? (locOverride !== undefined ? locOverride : reportLocation)
      : undefined;
    try {
      const { data } = await getCompletedRoutes(from, to, loc);
      const sorted = (data || []).slice().sort((a, b) => new Date(b.route_date) - new Date(a.route_date));
      setRoutes(sorted);
    } catch {
      setRoutes([]);
    }
    setLoading(false);
  };

  const currencyFormatter = new Intl.NumberFormat(lang === 'es' ? 'es-MX' : 'en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const fmt = (n) => currencyFormatter.format(parseFloat(n || 0));
  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d + 'T12:00:00').toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  // Per-route sales summary.
  const summarize = (route) => {
    const stops = route.route_stops || [];
    let charged = 0; let collected = 0; let done = 0;
    stops.forEach((s) => {
      charged += parseFloat(s.amount_charged) || 0;
      collected += (parseFloat(s.amount_paid) || 0) + (parseFloat(s.tip) || 0);
      if (s.completed) done += 1;
    });
    return { stops, count: stops.length, charged, collected, outstanding: Math.max(0, charged - collected), done };
  };

  // Group routes by day (already sorted desc).
  const byDate = {};
  routes.forEach((r) => { (byDate[r.route_date] = byDate[r.route_date] || []).push(r); });
  const dates = Object.keys(byDate);

  // Overall totals across every route in range.
  const totals = routes.reduce((acc, r) => {
    const s = summarize(r);
    acc.charged += s.charged; acc.collected += s.collected; acc.routes += 1;
    return acc;
  }, { charged: 0, collected: 0, routes: 0 });
  const totalOutstanding = Math.max(0, totals.charged - totals.collected);

  const downloadCSV = () => {
    const rows = [];
    rows.push(['MowStop — Sales summary by route']);
    rows.push([`Date range: ${from} to ${to}`]);
    rows.push([]);
    rows.push(['Date', 'Route', 'Stops', 'Completed', 'Charged', 'Collected', 'Outstanding']);
    routes.forEach((r) => {
      const s = summarize(r);
      rows.push([
        r.route_date,
        slotLabel(r.time_slot),
        s.count,
        s.done,
        s.charged.toFixed(2),
        s.collected.toFixed(2),
        s.outstanding.toFixed(2)
      ]);
    });
    rows.push([]);
    rows.push(['TOTALS', `${totals.routes} routes`, '', '',
      totals.charged.toFixed(2), totals.collected.toFixed(2), totalOutstanding.toFixed(2)]);
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mowstop-route-sales-${from}-to-${to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={styles.filterRow}>
        <div style={styles.filterField}>
          <label htmlFor={fromId} style={styles.filterLabel}>{t('reports.from')}</label>
          <input
            id={fromId}
            type="date"
            value={from}
            onChange={e => setFrom(e.target.value)}
            style={styles.dateInput}
          />
        </div>
        <div style={styles.filterField}>
          <label htmlFor={toId} style={styles.filterLabel}>{t('reports.to')}</label>
          <input
            id={toId}
            type="date"
            value={to}
            onChange={e => setTo(e.target.value)}
            style={styles.dateInput}
          />
        </div>
        {showLocationFilter && (
          <div style={styles.filterField}>
            <label htmlFor={locId} style={styles.filterLabel}>{t('reports.location')}</label>
            <select
              id={locId}
              value={reportLocation}
              onChange={(e) => { setReportLocation(e.target.value); loadReport(e.target.value); }}
              style={styles.dateInput}
            >
              <option value="all">{t('reports.all_locations')}</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        )}
        <button type="button" style={styles.searchBtn} onClick={() => loadReport()}>
          {t('reports.search')}
        </button>
      </div>

      {routes.length > 0 && (
        <div style={styles.summary} role="status" aria-live="polite">
          <div style={styles.summaryItem}>
            <div style={styles.summaryVal}>{totals.routes}</div>
            <div style={styles.summaryLabel}>{t('reports.routes')}</div>
          </div>
          <div style={styles.summaryItem}>
            <div style={styles.summaryVal}>{fmt(totals.charged)}</div>
            <div style={styles.summaryLabel}>{t('reports.charged')}</div>
          </div>
          <div style={styles.summaryItem}>
            <div style={{ ...styles.summaryVal, color: '#14653a' }}>{fmt(totals.collected)}</div>
            <div style={styles.summaryLabel}>{t('reports.collected')}</div>
          </div>
          <div style={styles.summaryItem}>
            <div style={{ ...styles.summaryVal, color: totalOutstanding > 0 ? '#dc2626' : '#14653a' }}>
              {fmt(totalOutstanding)}
            </div>
            <div style={styles.summaryLabel}>{t('reports.outstanding')}</div>
          </div>
        </div>
      )}

      {loading && <div style={styles.loading} role="status" aria-live="polite">{t('reports.loading')}</div>}
      {!loading && routes.length === 0 && (
        <div style={styles.empty} role="status">{t('reports.empty')}</div>
      )}

      {!loading && dates.map((d) => {
        const dayRoutes = byDate[d];
        const dayCharged = dayRoutes.reduce((s, r) => s + summarize(r).charged, 0);
        const dayCollected = dayRoutes.reduce((s, r) => s + summarize(r).collected, 0);
        return (
          <div key={d} style={styles.dayBlock}>
            <div style={styles.dayHeader}>
              <span style={styles.dayDate}>{formatDate(d)}</span>
              <span style={styles.dayTotals}>
                {fmt(dayCollected)} {t('reports.collected_short')}
                {dayCharged - dayCollected > 0 ? ` · ${fmt(dayCharged - dayCollected)} ${t('reports.out_short')}` : ''}
              </span>
            </div>

            {dayRoutes.map((route) => {
              const s = summarize(route);
              const open = !!expanded[route.id];
              const slotColor = SLOT_COLORS[route.time_slot] || '#5f6b58';
              const stopsLine = s.count === 1
                ? t('reports.stops_count_one', { n: s.count, done: s.done })
                : t('reports.stops_count_many', { n: s.count, done: s.done });
              return (
                <div key={route.id} style={styles.routeCard}>
                  <button
                    type="button"
                    style={styles.routeHead}
                    onClick={() => setExpanded(prev => ({ ...prev, [route.id]: !prev[route.id] }))}
                    aria-expanded={open}
                    aria-label={open ? t('reports.collapse_route') : t('reports.expand_route')}
                  >
                    <span style={styles.routeHeadLeft}>
                      <span style={{ ...styles.slotPill, background: slotColor }}>
                        {slotLabel(route.time_slot)}
                      </span>
                      <span style={styles.routeMeta}>{stopsLine}</span>
                    </span>
                    <span style={styles.routeAmounts}>
                      <span style={{ color: '#14653a', fontWeight: 600 }}>{fmt(s.collected)}</span>
                      {s.outstanding > 0 && (
                        <span style={{ color: '#dc2626' }}> · {fmt(s.outstanding)} {t('reports.out_short')}</span>
                      )}
                      <span style={styles.chev} aria-hidden="true">{open ? '▴' : '▾'}</span>
                    </span>
                  </button>

                  {open && (
                    <div style={styles.stopList}>
                      {s.stops.map((stop) => {
                        const charged = parseFloat(stop.amount_charged) || 0;
                        const collected = (parseFloat(stop.amount_paid) || 0) + (parseFloat(stop.tip) || 0);
                        const bal = Math.max(0, charged - collected);
                        return (
                          <div key={stop.id} style={styles.stopRow}>
                            <div style={styles.stopName}>{stop.clients?.name || '—'}</div>
                            <div style={styles.stopPay}>
                              {fmt(charged)} {t('reports.due')}
                              {collected > 0 && <span style={{ color: '#14653a' }}> · {fmt(collected)} {t('reports.paid')}</span>}
                              {bal > 0 && <span style={{ color: '#dc2626' }}> · {fmt(bal)} {t('reports.balance_short')}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {routes.length > 0 && (
        <button type="button" style={styles.downloadBtn} onClick={downloadCSV}>
          {t('reports.download_csv')}
        </button>
      )}
    </div>
  );
}

const styles = {
  filterRow: { display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' },
  filterField: { display: 'flex', flexDirection: 'column', gap: 4 },
  filterLabel: { fontSize: 11, fontWeight: 500, color: '#5f6b58' },
  dateInput: { padding: '7px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 },
  searchBtn: { padding: '7px 16px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 13, fontWeight: 500, color: '#fff', cursor: 'pointer' },
  summary: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 16 },
  summaryItem: { background: '#f9f9f9', borderRadius: 8, padding: '8px 6px', textAlign: 'center' },
  summaryVal: { fontSize: 15, fontWeight: 500, color: '#1a1a1a' },
  summaryLabel: { fontSize: 10, color: '#5f6b58', marginTop: 2 },
  loading: { textAlign: 'center', padding: 32, color: '#5f6b58' },
  empty: { textAlign: 'center', padding: 40, color: '#5f6b58', fontSize: 14, background: '#f9f9f9', borderRadius: 10 },
  dayBlock: { marginBottom: 16 },
  dayHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 2px 8px', borderBottom: '1px solid #eee', marginBottom: 8 },
  dayDate: { fontSize: 13, fontWeight: 600, color: '#1a1a1a' },
  dayTotals: { fontSize: 12, color: '#5f6b58' },
  routeCard: { border: '1px solid #eee', borderRadius: 10, marginBottom: 8, background: '#fff', overflow: 'hidden' },
  routeHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 12px', cursor: 'pointer', width: '100%', background: '#fff', border: 'none', textAlign: 'left', font: 'inherit' },
  routeHeadLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  slotPill: { fontSize: 11, color: '#fff', padding: '3px 9px', borderRadius: 20, fontWeight: 600 },
  routeMeta: { fontSize: 12.5, color: '#5f6b58' },
  routeAmounts: { fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 4 },
  chev: { color: '#5f6b58', marginLeft: 6 },
  stopList: { borderTop: '1px solid #f0f0f0', padding: '4px 12px 8px', background: '#fafafa' },
  stopRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f0f0f0', gap: 10 },
  stopName: { fontSize: 13, color: '#1a1a1a', fontWeight: 500 },
  stopPay: { fontSize: 11.5, color: '#5f6b58', textAlign: 'right' },
  downloadBtn: { width: '100%', padding: '12px', border: 'none', background: '#1a1a1a', borderRadius: 8, fontSize: 13, fontWeight: 500, color: '#fff', cursor: 'pointer', marginTop: 8 }
};
